/**
 * The client lifecycle after the first meeting: proposals with an engagement letter, recurring
 * billing, renewals, offboarding and review requests. Prices are set per client on each proposal;
 * there is no price list.
 */
import { z } from "zod";
import { MODULES } from "./roles";

export const PROPOSAL_BILLING = ["one_time", "monthly", "quarterly", "annual"] as const;
export type ProposalBillingKind = (typeof PROPOSAL_BILLING)[number];
export const PROPOSAL_BILLING_LABELS: Record<ProposalBillingKind, string> = {
  one_time: "One-time",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annually",
};
export type ScheduleInterval = "month" | "quarter" | "year";
export const BILLING_INTERVAL: Record<Exclude<ProposalBillingKind, "one_time">, ScheduleInterval> = { monthly: "month", quarterly: "quarter", annual: "year" };
export const INTERVAL_MONTHS: Record<ScheduleInterval, number> = { month: 1, quarter: 3, year: 12 };
export const INTERVAL_LABELS: Record<ScheduleInterval, string> = { month: "a month", quarter: "a quarter", year: "a year" };

export const OFFBOARDING_REASONS = ["client_request", "firm_decision", "nonpayment", "business_closed", "other"] as const;
export const OFFBOARDING_REASON_LABELS: Record<(typeof OFFBOARDING_REASONS)[number], string> = {
  client_request: "The client asked to end the engagement",
  firm_decision: "The firm decided to end the engagement",
  nonpayment: "Unpaid fees",
  business_closed: "The business closed",
  other: "Other",
};

/** The consent the client gives when they turn on automatic payment. */
export const AUTOPAY_CONSENT_VERSION = "autopay-2026-10";
export const AUTOPAY_CONSENT_TEXT =
  "I authorize the firm to charge this payment method for each recurring invoice on the day it is issued, for the amount of that invoice, until I turn automatic payment off. I will be emailed each invoice before or when it is charged. I can turn automatic payment off or change the payment method in the portal at any time.";

/** The default engagement letter terms. {{firm}} and {{client}} are filled in when the letter is made. */
export const DEFAULT_ENGAGEMENT_TERMS = `This letter confirms the services {{firm}} ("the firm") will provide to {{client}} ("you"), the fees for them, and the responsibilities of each of us.

Scope of services
The firm will provide the services listed in this proposal, for the term stated. Any service not listed (for example, representation before a tax authority, amended returns, or work for another entity) is outside this engagement and will be agreed and priced separately before it begins.

Your responsibilities
You are responsible for the completeness and accuracy of the information you give us, for keeping your records, and for reviewing and approving each return or report before it is filed or issued. We may rely on the information you provide without auditing or verifying it, unless this proposal says otherwise.

Fees and billing
Fees are as listed in this proposal. Recurring fees are invoiced on the schedule shown and are due within the number of days stated on each invoice. You may pay online by card or bank account, by automatic payment from a saved card or bank account, or by check or other method the firm accepts. Work may be paused while an invoice is more than 30 days past due.

Term and renewal
This engagement runs for the term stated in the proposal. Before the term ends, the firm will send a renewal proposal with any change in services or fees for the next term. Recurring services continue at the current fees until the renewal is accepted or the engagement ends.

Ending the engagement
Either of us may end this engagement by written notice. Fees for work performed up to the date of notice remain due. When the engagement ends, the firm will return any original documents you provided and will keep its own records as required by law and professional standards.

Confidentiality and records
The firm keeps your information confidential and uses it only to provide these services, except as required by law. Consent to use or disclose tax return information, where required, is requested separately.

Electronic signature
By accepting this proposal electronically, you agree to the services, fees and terms above. Your typed name is your signature and has the same effect as a handwritten signature.`;

const cents = z.number().int().min(0).max(99_999_999);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const lifecycleSettingsSchema = z
  .object({
    // The engagement letter's terms for new proposals (each proposal can still be edited).
    terms: z.string().trim().min(1).max(40_000),
    // How long a sent proposal can be accepted.
    validDays: z.number().int().min(1).max(120),
    // Days to pay a recurring invoice (autopay charges on the issue date regardless).
    dueDays: z.number().int().min(0).max(90),
    // How long before a term ends its renewal is drafted.
    renewalLeadDays: z.number().int().min(14).max(120),
    // Days after a failed automatic payment before it is tried once more.
    autopayRetryDays: z.number().int().min(1).max(14),
    reviews: z
      .object({
        enabled: z.boolean(),
        // The firm's public review page (Google, for example).
        url: z.string().trim().url().startsWith("https://").max(500).nullable(),
        // Days after the work is completed before the request is sent.
        delayDays: z.number().int().min(0).max(30),
      })
      .strict(),
  })
  .strict();
export type LifecycleSettings = z.infer<typeof lifecycleSettingsSchema>;

export const DEFAULT_LIFECYCLE_SETTINGS: LifecycleSettings = {
  terms: DEFAULT_ENGAGEMENT_TERMS,
  validDays: 30,
  dueDays: 10,
  renewalLeadDays: 45,
  autopayRetryDays: 3,
  reviews: { enabled: false, url: null, delayDays: 3 },
};

/** The firm's lifecycle settings, each part falling back to the default when not set. */
export function lifecycleSettings(raw: unknown): LifecycleSettings {
  const parsed = lifecycleSettingsSchema.partial().safeParse(raw ?? {});
  const v = parsed.success ? parsed.data : {};
  return { ...DEFAULT_LIFECYCLE_SETTINGS, ...v, reviews: { ...DEFAULT_LIFECYCLE_SETTINGS.reviews, ...(v.reviews ?? {}) } };
}

export const proposalItemSchema = z
  .object({
    module: z.enum(MODULES),
    description: z.string().trim().min(1).max(300),
    billing: z.enum(PROPOSAL_BILLING),
    amountCents: cents,
    templateId: z.string().uuid().nullable().optional(),
    period: z.string().trim().min(1).max(20).nullable().optional(),
  })
  .strict();

export const proposalSchema = z
  .object({
    clientId: z.string().uuid(),
    title: z.string().trim().min(1).max(200),
    message: z.string().trim().max(4000).default(""),
    terms: z.string().trim().min(1).max(40_000).optional(),
    startsOn: day,
    termMonths: z.number().int().min(1).max(36).default(12),
    items: z.array(proposalItemSchema).min(1).max(30),
  })
  .strict();
export const updateProposalSchema = proposalSchema.omit({ clientId: true });

export const acceptProposalSchema = z.object({ name: z.string().trim().min(1).max(200), agree: z.literal(true) }).strict();
export const declineProposalSchema = z.object({ note: z.string().trim().max(2000).default("") }).strict();

export const scheduleSchema = z
  .object({
    clientId: z.string().uuid(),
    module: z.enum(MODULES),
    description: z.string().trim().min(1).max(300),
    amountCents: z.number().int().min(1).max(99_999_999),
    interval: z.enum(["month", "quarter", "year"]),
    nextOn: day,
    endsOn: day.nullable().optional(),
    dueDays: z.number().int().min(0).max(90).optional(),
  })
  .strict();
export const updateScheduleSchema = z
  .object({
    description: z.string().trim().min(1).max(300).optional(),
    amountCents: z.number().int().min(1).max(99_999_999).optional(),
    nextOn: day.optional(),
    endsOn: day.nullable().optional(),
    status: z.enum(["active", "paused"]).optional(),
    dueDays: z.number().int().min(0).max(90).optional(),
  })
  .strict();

export const autopaySchema = z.object({ methodId: z.string().uuid(), name: z.string().trim().min(1).max(200), agree: z.literal(true) }).strict();

export const offboardSchema = z
  .object({
    effectiveOn: day,
    reason: z.enum(OFFBOARDING_REASONS),
    note: z.string().trim().max(2000).default(""),
    // The services that end; all of the client's services when every one is listed.
    modules: z.array(z.enum(MODULES)).min(1).max(4),
    // Open work in those services is cancelled (otherwise it stays open to be finished).
    cancelOpenWork: z.boolean(),
    portalAccess: z.enum(["keep", "documents_only", "removed"]),
    sendLetter: z.boolean(),
  })
  .strict();

// --- Dates --------------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, "0");
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** The date `months` months after `from`, on `anchorDay` (or the month's last day when shorter). */
export function addMonthsOn(from: string, months: number, anchorDay: number): string {
  const [y, m] = from.split("-").map(Number) as [number, number];
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${pad(nm)}-${pad(Math.min(anchorDay, daysIn(ny, nm)))}`;
}

export function addDaysTo(from: string, days: number): string {
  const d = new Date(`${from}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The last day of a term that starts on `startsOn` and runs `termMonths` months. */
export function termEnd(startsOn: string, termMonths: number): string {
  const day = Number(startsOn.slice(8, 10));
  return addDaysTo(addMonthsOn(startsOn, termMonths, day), -1);
}

/** A schedule's next invoice date after `current`. */
export function nextBillingDate(current: string, interval: ScheduleInterval, anchorDay: number): string {
  return addMonthsOn(current, INTERVAL_MONTHS[interval], anchorDay);
}

/** What a set of items comes to: once, and each year from the recurring fees. */
export function proposalTotals(items: Array<{ billing: ProposalBillingKind; amountCents: number }>): { oneTimeCents: number; annualCents: number; firstYearCents: number } {
  let oneTimeCents = 0;
  let annualCents = 0;
  for (const i of items) {
    if (i.billing === "one_time") oneTimeCents += i.amountCents;
    else annualCents += i.amountCents * (12 / INTERVAL_MONTHS[BILLING_INTERVAL[i.billing]]);
  }
  return { oneTimeCents, annualCents, firstYearCents: oneTimeCents + annualCents };
}

/** Fills {{firm}} and {{client}} in letter text. */
export function fillTerms(terms: string, v: { firm: string; client: string }): string {
  return terms.replaceAll("{{firm}}", v.firm).replaceAll("{{client}}", v.client);
}
